import { subDays, format, differenceInCalendarDays } from 'date-fns';

// Coerce Firestore Timestamp | Date | ISO string -> Date | null
export function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function bucketByDay(items, getDate, days = 30, today = new Date()) {
  const labels = Array.from({ length: days }, (_, i) =>
    format(subDays(today, days - 1 - i), 'yyyy-MM-dd')
  );
  const counts = labels.map(() => 0);
  const index = new Map(labels.map((l, i) => [l, i]));
  for (const it of items) {
    const d = toDate(getDate(it));
    if (!d) continue;
    const key = format(d, 'yyyy-MM-dd');
    if (index.has(key)) counts[index.get(key)] += 1;
  }
  return { labels, counts };
}

export function computeUserKpis(users, today = new Date()) {
  const total = users.length;
  const adminCount = users.filter((u) => u.role === 'Admin').length;
  const proCount = users.filter((u) => u.role === 'Pro').length;
  const freeCount = users.filter((u) => u.role === 'Free').length;
  const within = (u, n) => {
    const d = toDate(u.createdAt);
    return d && differenceInCalendarDays(today, d) <= n && differenceInCalendarDays(today, d) >= 0;
  };
  return {
    total,
    adminCount,
    proCount,
    freeCount,
    proConversion: total ? Math.round((proCount / total) * 100) : 0,
    newLast7: users.filter((u) => within(u, 7)).length,
    newLast30: users.filter((u) => within(u, 30)).length,
  };
}

export function canChangeRole({ targetId, targetRole, newRole, admins, actorUid }) {
  if (targetRole === 'Admin' && newRole !== 'Admin') {
    const remaining = admins.filter((a) => a.id !== targetId).length;
    if (remaining < 1) {
      return { ok: false, reason: "Non puoi rimuovere l'ultimo amministratore." };
    }
  }
  if (targetId === actorUid && targetRole === 'Admin' && newRole !== 'Admin') {
    return { ok: false, reason: 'Non puoi rimuovere i tuoi stessi privilegi di amministratore.' };
  }
  return { ok: true };
}

export function canDeleteUser({ targetId, targetRole, admins, actorUid }) {
  if (targetId === actorUid) {
    return { ok: false, reason: 'Non puoi eliminare il tuo stesso account.' };
  }
  if (targetRole === 'Admin' && admins.filter((a) => a.id !== targetId).length < 1) {
    return { ok: false, reason: "Non puoi eliminare l'ultimo amministratore." };
  }
  return { ok: true };
}

export function filterAndSortUsers(users, { search = '', roleFilter = 'all', sortBy = 'createdAt', sortDir = 'desc' } = {}) {
  const q = search.trim().toLowerCase();
  let rows = users.filter((u) => {
    const matchesText = !q || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesText && matchesRole;
  });
  rows = [...rows].sort((a, b) => {
    let av = a[sortBy];
    let bv = b[sortBy];
    if (sortBy === 'createdAt') {
      av = toDate(av)?.getTime() || 0;
      bv = toDate(bv)?.getTime() || 0;
    } else {
      av = (av || '').toString().toLowerCase();
      bv = (bv || '').toString().toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  return rows;
}

export function buildAuditEntry({ actorUid, actorEmail, action, targetType, targetId, before = null, after = null }) {
  return { actorUid, actorEmail, action, targetType, targetId, before, after };
}

// Fields to copy from a useCaseSubmissions doc into publicUseCases on
// approval (createdAt handled separately by the caller, since it needs a
// Firestore Timestamp default). anonymizedSummary_en is omitted entirely
// when absent — Firestore setDoc rejects an explicit `undefined`, and
// UseCaseShowcase already falls back to anonymizedSummary when the EN key
// is missing — so leaving it out, rather than writing null, is equivalent
// and simpler.
export function buildPublicUseCasePayload(useCase) {
  const payload = {
    sector: useCase.sector || '',
    anonymizedSummary: useCase.anonymizedSummary,
  };
  if (useCase.anonymizedSummary_en) {
    payload.anonymizedSummary_en = useCase.anonymizedSummary_en;
  }
  return payload;
}

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const lines = rows.map((r) => columns.map((c) => csvCell(r[c.key])).join(','));
  return [header, ...lines].join('\n');
}
